/** União de fontes (IndexedDB + localStorage) para recuperar dados parcialmente apagados. */

import { extrairNumeroLote } from './adePersistenciaLotes';

export function normalizarChavePersistencia(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

export function lerArraysLegadoLocalStorage(chaves: readonly string[]): unknown[][] {
  const fontes: unknown[][] = [];
  for (const key of chaves) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) fontes.push(parsed);
    } catch {
      /* ignora */
    }
  }
  return fontes;
}

export type OpcoesMesclaRegistros<T extends Record<string, unknown>> = {
  getId?: (item: T) => string;
  getChaveFallback?: (item: T) => string;
  pontuacao?: (item: T) => number;
  ordenar?: (a: T, b: T) => number;
};

export function mesclarRegistrosPorId<T extends Record<string, unknown>>(
  listas: T[][],
  opcoes?: OpcoesMesclaRegistros<T>
): T[] {
  const getId = opcoes?.getId ?? ((item) => String(item.id || '').trim());
  const map = new Map<string, T>();

  for (const lista of listas) {
    if (!Array.isArray(lista)) continue;
    for (const bruto of lista) {
      if (!bruto || typeof bruto !== 'object') continue;
      const item = bruto as T;
      const id = getId(item);
      const k = id ? `id:${id}` : opcoes?.getChaveFallback?.(item) ?? '';
      if (!k) continue;
      const prev = map.get(k);
      const score = opcoes?.pontuacao?.(item) ?? 1;
      const prevScore = prev ? (opcoes?.pontuacao?.(prev) ?? 1) : -1;
      if (!prev || score >= prevScore) map.set(k, item);
    }
  }

  const result = [...map.values()];
  if (opcoes?.ordenar) result.sort(opcoes.ordenar);
  return result;
}

export function resumoRegistrosComLote<T extends { lote?: string }>(
  lista: T[]
): { total: number; maiorNumeroLote: number } {
  let maior = 0;
  for (const item of lista) {
    const n = extrairNumeroLote(String(item.lote || ''));
    if (n > maior) maior = n;
  }
  return { total: lista.length, maiorNumeroLote: maior };
}

/** Repõe registros com lote maior que o máximo atual (ex.: parou em 8710, backup tem até 8808). */
export function recuperarRegistrosComLoteMaior<T extends { id?: string; lote?: string }>(
  items: T[],
  existente: T[],
  getNome?: (item: T) => string
): T[] {
  if (!existente.length) return items;
  const maxItems = Math.max(0, ...items.map((p) => extrairNumeroLote(String(p.lote || ''))));
  const ids = new Set(items.map((p) => String(p.id || '').trim()).filter(Boolean));
  const chaves = new Set(
    items.map((p) => {
      const nome = getNome ? getNome(p) : '';
      return `${normalizarChavePersistencia(String(p.lote || ''))}|${normalizarChavePersistencia(nome)}`;
    })
  );

  const extras = existente.filter((lp) => {
    const id = String(lp.id || '').trim();
    if (id && ids.has(id)) return false;
    const nome = getNome ? getNome(lp) : '';
    const ck = `${normalizarChavePersistencia(String(lp.lote || ''))}|${normalizarChavePersistencia(nome)}`;
    if (chaves.has(ck)) return false;
    return extrairNumeroLote(String(lp.lote || '')) > maxItems;
  });

  if (!extras.length) return items;

  return mesclarRegistrosPorId([items, extras] as Record<string, unknown>[][], {
    getId: (i) => String(i.id || '').trim(),
    getChaveFallback: (i) => {
      const nome = getNome ? getNome(i as T) : String(i.nome || i.nomeProduto || '');
      return `fb:${normalizarChavePersistencia(String(i.lote || ''))}|${normalizarChavePersistencia(nome)}`;
    },
    ordenar: (a, b) =>
      extrairNumeroLote(String(a.lote || '')) - extrairNumeroLote(String(b.lote || '')),
  }) as T[];
}

export const CHAVES_PRODUTOS_TEMPO_CORTE = ['produtos-tempo-corte-ade'] as const;
export const CHAVES_TEMPO_CORTE_ITENS = ['tempo-corte-itens-ade'] as const;
export const CHAVES_HISTORICO_IMPRESSOES = ['historico-impressoes-ade'] as const;
export const CHAVES_SOBRA_PECAS = ['sobra-pecas-ade'] as const;
export const CHAVES_SOBRA_PASTAS = ['sobra-pastas-ade'] as const;
export const CHAVES_SOBRA_RISCADOS = ['sobra-riscados-ade'] as const;

export function pontuacaoProdutoTempoCorte(item: Record<string, unknown>): number {
  // Não pontuar tempoExato>0: cards com peças todas desmarcadas (tempoExato=0)
  // perderiam no merge/sync para a versão antiga com o tempo total.
  return (
    (item.dataHoraTerminoProducao ? 2 : 0) +
    (item.dataHoraInicioProducao ? 1 : 0) +
    (String(item.id || '').trim() ? 1 : 0)
  );
}

/** Nome do card mantendo MDF/AGLO (NICHO ≠ MDF NICHO). */
function chaveNomeProdutoTempoCortePersistencia(nome: string): string {
  return normalizarChavePersistencia(nome)
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\b(\d+)\.(\d*?)0+\b/g, (_m, intPart: string, decPart: string) => {
      const trimmed = decPart.replace(/0+$/, '');
      return trimmed ? `${intPart}.${trimmed}` : intPart;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

export function chaveLoteNomeProdutoTempoCorte(lote: string, nome: string): string {
  return `${normalizarChavePersistencia(lote)}|${chaveNomeProdutoTempoCortePersistencia(nome)}`;
}

/**
 * Remove cards duplicados (mesmo lote + mesmo nome, IDs diferentes).
 * Causa típica: "Criar cards faltantes" disparado 2× antes do estado atualizar.
 * Mantém o card com mais dados / criado primeiro.
 */
export function deduplicarProdutosTempoCortePorLoteNome<
  T extends { id?: string; nome?: string; lote?: string; itens?: unknown[] },
>(lista: T[]): T[] {
  if (!Array.isArray(lista) || lista.length <= 1) return lista;
  const map = new Map<string, T>();
  for (const card of lista) {
    if (!card || typeof card !== 'object') continue;
    const lote = String(card.lote || '').trim();
    const nome = String(card.nome || '').trim();
    if (!lote || !nome) {
      const id = String(card.id || '').trim();
      map.set(id ? `id:${id}` : `row:${map.size}`, card);
      continue;
    }
    const k = chaveLoteNomeProdutoTempoCorte(lote, nome);
    const prev = map.get(k);
    if (!prev) {
      map.set(k, card);
      continue;
    }
    const score = (c: T) => {
      const itens = Array.isArray(c.itens) ? c.itens.length : 0;
      const base = pontuacaoProdutoTempoCorte(c as Record<string, unknown>);
      const analise = (c as { enviarParaAnalise?: boolean }).enviarParaAnalise ? 5 : 0;
      const iso = String((c as { dataCriacaoISO?: string }).dataCriacaoISO || '');
      const t = Date.parse(iso);
      // Mais dados vence; empate → o mais antigo (primeira criação).
      return base * 1000 + analise * 100 + itens * 10 - (Number.isFinite(t) ? t / 1e15 : 0);
    };
    if (score(card) > score(prev)) map.set(k, card);
  }
  const result = [...map.values()];
  result.sort(ordenarProdutosTempoCortePorLote as (a: T, b: T) => number);
  return result;
}

export function ordenarProdutosTempoCortePorLote(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): number {
  const na = extrairNumeroLote(String(a.lote || ''));
  const nb = extrairNumeroLote(String(b.lote || ''));
  if (na !== nb) return na - nb;
  return String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');
}

export function mesclarProdutosTempoCorte(listas: Record<string, unknown>[][]): Record<string, unknown>[] {
  const mesclados = mesclarRegistrosPorId(listas, {
    getId: (i) => String(i.id || '').trim(),
    getChaveFallback: (i) => {
      const nome = String(i.nome || i.nomeProduto || '');
      return `fb:${chaveLoteNomeProdutoTempoCorte(String(i.lote || ''), nome)}`;
    },
    pontuacao: pontuacaoProdutoTempoCorte,
    ordenar: ordenarProdutosTempoCortePorLote,
  });
  // IDs diferentes + mesmo lote|nome (ex.: auto-create em dobro) → um só.
  return deduplicarProdutosTempoCortePorLoteNome(mesclados);
}
